import { normalizeMapStyleSourcesForRequest } from '@workspace/ui/src/utils/map-style-sources';
import type { MapExperienceResolverExecutionRequest, RuntimeFeatureStatePatch } from '@workspace/ui';

export interface BasemapSummary {
  id: string;
  name?: string;
  description?: string;
  share_scope?: string;
  style_theme_id?: string | null;
  renderer?: string;
  status?: string;
  layer_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface LayerSummary {
  id: string;
  name?: string;
  description?: string;
  geometry_types?: string[];
  updated_at?: string;
  artifacts?: LayerArtifact[];
}

export interface LayerArtifact {
  artifact_type?: string;
  metadata?: Record<string, unknown>;
}

export interface MapViewSummary {
  id: string;
  name?: string;
  description?: string;
  renderer?: string;
  share_scope?: string;
  is_enabled?: boolean;
  updated_at?: string;
}

export interface RunAccepted {
  id?: string;
  status_url?: string;
}

export interface ConnectorSummary {
  id: string;
  name?: string;
  slug?: string;
  connector_type?: string;
  source_uri?: string | null;
  refresh_seconds?: number | null;
  auth?: Record<string, unknown> | null;
  last_fetch_at?: string | null;
  last_status?: string | null;
  last_error?: string | null;
  last_run_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface IngestionConfigSummary {
  id: string;
  name?: string;
  enabled?: boolean;
  schedule_cron?: string;
  source_layer_id?: string | null;
  external_source_id?: string | null;
  source_type?: string | null;
  source_uri?: string | null;
  source_format?: string | null;
  ingest_strategy?: string | null;
  minzoom?: number | null;
  maxzoom?: number | null;
  last_run_id?: string | null;
  last_run_status?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface GeoJSONImportResponse {
  layer?: LayerSummary;
  run?: RunAccepted;
}

interface BasemapListResponse {
  data?: BasemapSummary[];
}

interface BasemapResponse {
  data?: BasemapSummary;
}

interface LayerListResponse {
  data?: LayerSummary[];
}

interface MapViewListResponse {
  data?: MapViewSummary[];
}

interface ConnectorListResponse {
  data?: ConnectorSummary[];
}

interface IngestionConfigListResponse {
  data?: IngestionConfigSummary[];
}

export type ShareScope = 'tenant' | 'shared' | 'public';
export type MapSourceKind = 'pmtiles' | 'geojson_managed' | 'geojson_http';
export type GeoJSONHTTPAuthStrategy = 'none' | 'bearer_token' | 'header_api_key';

export interface GeoJSONHTTPAuthMetadata {
  header_name?: string | null;
}

interface BasemapDefinitionConfig {
  metadata?: Record<string, unknown>;
}

interface BasemapLayerInput {
  layer_id: string;
  ordering?: number;
  overrides?: Record<string, unknown> | null;
}

export interface BasemapCreateRequest {
  name: string;
  description?: string | null;
  renderer: 'maplibre';
  share_scope: ShareScope;
  style_theme_id?: string | null;
  basemap_config?: BasemapDefinitionConfig | null;
  layers: BasemapLayerInput[];
}

export interface BasemapUpdateRequest {
  name?: string;
  description?: string | null;
  status?: 'draft' | 'active' | 'deprecated';
  share_scope?: ShareScope;
  style_theme_id?: string | null;
  basemap_config?: BasemapDefinitionConfig | null;
  layers?: BasemapLayerInput[];
}

interface StyleThemeListResponse {
  data?: StyleTheme[];
}

export interface StyleTheme {
  id?: string;
  name?: string;
  description?: string | null;
  share_scope?: ShareScope;
  renderer_support?: string[];
  theme_json?: Record<string, unknown>;
  google_map_id?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at?: string;
  updated_at?: string;
}

export interface StyleThemeCreateRequest {
  name: string;
  description?: string | null;
  share_scope: ShareScope;
  theme_json: Record<string, unknown>;
  renderer_support?: string[];
  google_map_id?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface StyleThemeUpdateRequest {
  name?: string;
  description?: string | null;
  share_scope?: ShareScope;
  theme_json?: Record<string, unknown>;
  renderer_support?: string[];
  google_map_id?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface StyleThemePreviewRequest {
  layer_id: string;
  theme_json: Record<string, unknown>;
  metadata?: Record<string, unknown> | null;
}

export interface MapViewState {
  center?: [number, number];
  zoom?: number;
  bearing?: number;
  pitch?: number;
}

export interface StyleThemePreviewResponse {
  style_json?: Record<string, unknown> | null;
  view_state?: MapViewState | null;
}

export type MapComponentFilterFieldValueType = 'string' | 'number' | 'boolean' | 'date' | 'unknown';

export interface MapComponentFilterOperator {
  id: string;
  label: string;
  description?: string | null;
  input: string;
}

export interface MapComponentFilterOption {
  value: string;
  label: string;
  description?: string | null;
}

export interface MapComponentFilterField {
  id: string;
  label: string;
  description?: string | null;
  value_type: MapComponentFilterFieldValueType;
  operators: MapComponentFilterOperator[];
  options?: MapComponentFilterOption[];
  allow_multiple?: boolean;
  total_distinct?: number;
}

export type MapComponentFilterColumnType = 'select' | 'text' | 'number' | 'boolean';

export interface MapComponentFilterColumn {
  key: string;
  label: string;
  type?: MapComponentFilterColumnType;
  options?: string[];
  totalDistinct: number;
}

export interface MapComponentMetadataResponse {
  datasource_key?: string;
  columns?: MapComponentFilterColumn[];
  fields?: MapComponentFilterField[];
  capabilities?: Record<string, unknown> | null;
}

export interface TenantShare {
  tenant_id: string;
  created_at?: string;
  created_by?: string | null;
}

interface TenantShareListResponse {
  data?: TenantShare[];
}

export interface SpriteManifestEntry {
  width: number;
  height: number;
  x: number;
  y: number;
  pixelRatio?: number;
  sdf?: boolean;
}

export type SpriteManifest = Record<string, SpriteManifestEntry>;

async function requestJson<T>(path: string, init: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers ?? {})
    },
    ...init
  });

  if (response.status === 401) {
    throw new Error('not authenticated');
  }

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Request failed (${response.status}): ${text}`);
  }

  if (response.status === 204) {
    return {} as T;
  }

  return (await response.json()) as T;
}

async function fetchJson<T>(path: string): Promise<T> {
  return requestJson<T>(path, { method: 'GET' });
}

async function fetchBlob(path: string): Promise<Blob> {
  const response = await fetch(path, {
    credentials: 'include',
  });

  if (response.status === 401) {
    throw new Error('not authenticated');
  }

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Request failed (${response.status}): ${text}`);
  }

  return await response.blob();
}

async function requestMultipart<T>(path: string, init: Omit<RequestInit, 'body'> & { body?: FormData }): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init.headers ?? {})
    },
    ...init
  });

  if (response.status === 401) {
    throw new Error('not authenticated');
  }

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Request failed (${response.status}): ${text}`);
  }

  if (response.status === 204) {
    return {} as T;
  }

  return (await response.json()) as T;
}

function buildQuery(params?: Record<string, string | number | undefined>): string {
  if (!params) {
    return '';
  }
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) {
      continue;
    }
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export interface MappingQueryContext {
  catalog_owner_tenant_id?: string | null;
  entitlement_id?: string | null;
}

function buildMappingQueryParams(
  params?: Record<string, string | number | undefined>,
  context?: MappingQueryContext | null,
): string {
  return buildQuery({
    ...params,
    sales_context_catalog_owner_tenant_id: context?.catalog_owner_tenant_id ?? undefined,
    sales_context_entitlement_id: context?.entitlement_id ?? undefined,
  });
}

function isMappingRuntimeURL(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  if (trimmed.startsWith('/api/v1/map-sources/') || trimmed.startsWith('/api/v1/mapping/map-sources/')) {
    return true;
  }
  try {
    const parsed = new URL(trimmed, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    return (
      parsed.pathname.startsWith('/api/v1/map-sources/') ||
      parsed.pathname.startsWith('/api/v1/mapping/map-sources/')
    );
  } catch {
    return false;
  }
}

function appendSalesContextToURL(value: string, context?: MappingQueryContext | null): string {
  const trimmed = value.trim();
  if (!trimmed || !context?.catalog_owner_tenant_id) {
    return value;
  }
  if (!isMappingRuntimeURL(trimmed)) {
    return value;
  }
  try {
    if (trimmed.startsWith('/')) {
      const parsed = new URL(trimmed, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
      parsed.searchParams.set('sales_context_catalog_owner_tenant_id', context.catalog_owner_tenant_id);
      if (context.entitlement_id) {
        parsed.searchParams.set('sales_context_entitlement_id', context.entitlement_id);
      }
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
    const parsed = new URL(trimmed);
    parsed.searchParams.set('sales_context_catalog_owner_tenant_id', context.catalog_owner_tenant_id);
    if (context.entitlement_id) {
      parsed.searchParams.set('sales_context_entitlement_id', context.entitlement_id);
    }
    return parsed.toString();
  } catch {
    return value;
  }
}

function appendSalesContextToSourceReference(value: string, prefix: string, context?: MappingQueryContext | null): string {
  if (!value.startsWith(prefix)) {
    return value;
  }
  const inner = value.slice(prefix.length);
  const rewritten = appendSalesContextToURL(inner, context);
  return rewritten === inner ? value : `${prefix}${rewritten}`;
}

function rewriteMapStyleRuntimeSources(style: MapStyle, context?: MappingQueryContext | null): MapStyle {
  if (!context?.catalog_owner_tenant_id) {
    return style;
  }
  const styleRoot =
    style.style_json && typeof style.style_json === 'object'
      ? (style.style_json as Record<string, unknown>)
      : null;
  const sources =
    styleRoot?.sources && typeof styleRoot.sources === 'object'
      ? (styleRoot.sources as Record<string, Record<string, unknown>>)
      : null;
  if (!styleRoot || !sources) {
    return style;
  }

  let changed = false;
  const nextSources: Record<string, Record<string, unknown>> = {};
  for (const [key, source] of Object.entries(sources)) {
    const nextSource: Record<string, unknown> = { ...source };
    if (typeof nextSource.url === 'string') {
      const rewritten =
        appendSalesContextToSourceReference(nextSource.url, 'pmtiles://', context) !== nextSource.url
          ? appendSalesContextToSourceReference(nextSource.url, 'pmtiles://', context)
          : appendSalesContextToURL(nextSource.url, context);
      if (rewritten !== nextSource.url) {
        nextSource.url = rewritten;
        changed = true;
      }
    }
    if (typeof nextSource.data === 'string') {
      const rewritten =
        appendSalesContextToSourceReference(nextSource.data, 'geojson://', context) !== nextSource.data
          ? appendSalesContextToSourceReference(nextSource.data, 'geojson://', context)
          : appendSalesContextToURL(nextSource.data, context);
      if (rewritten !== nextSource.data) {
        nextSource.data = rewritten;
        changed = true;
      }
    }
    if (Array.isArray(nextSource.tiles)) {
      const rewrittenTiles = nextSource.tiles.map(tile =>
        typeof tile === 'string' ? appendSalesContextToURL(tile, context) : tile,
      );
      if (JSON.stringify(rewrittenTiles) !== JSON.stringify(nextSource.tiles)) {
        nextSource.tiles = rewrittenTiles;
        changed = true;
      }
    }
    nextSources[key] = nextSource;
  }
  if (!changed) {
    return style;
  }
  return {
    ...style,
    style_json: {
      ...styleRoot,
      sources: nextSources,
    },
  };
}

type LimitParam = number | undefined;

type BasemapListParams = LimitParam | { limit?: number; share_scope?: string };
type LayerListParams = LimitParam | { limit?: number; type?: string; geometry_type?: string; share_scope?: string };
type MapViewListParams = LimitParam | { limit?: number; share_scope?: string };

function normalizeListParams<T extends Record<string, unknown>>(input?: number | T): T {
  if (typeof input === 'number') {
    return { limit: input } as unknown as T;
  }
  return (input ?? {}) as T;
}

export async function listBasemaps(params?: BasemapListParams): Promise<BasemapSummary[]> {
  const options = normalizeListParams<{ limit?: number; share_scope?: string }>(params);
  const query = buildQuery({ limit: options.limit ?? 50, share_scope: options.share_scope });
  const payload = await fetchJson<BasemapListResponse>(`/api/v1/mapping/basemaps${query}`);
  return payload.data ?? [];
}

export async function listLayers(params?: LayerListParams): Promise<LayerSummary[]> {
  const options = normalizeListParams<{ limit?: number; type?: string; geometry_type?: string; share_scope?: string }>(params);
  const query = buildQuery({
    limit: options.limit ?? 50,
    type: options.type,
    geometry_type: options.geometry_type,
    share_scope: options.share_scope
  });
  const payload = await fetchJson<LayerListResponse>(`/api/v1/mapping/layers${query}`);
  return payload.data ?? [];
}

export async function listMapViews(params?: MapViewListParams): Promise<MapViewSummary[]> {
  const options = normalizeListParams<{ limit?: number; share_scope?: string }>(params);
  const query = buildQuery({ limit: options.limit ?? 50, share_scope: options.share_scope });
  const payload = await fetchJson<MapViewListResponse>(`/api/v1/mapping/map-views${query}`);
  return payload.data ?? [];
}

export async function createLayer(payload: { name: string; description?: string | null }): Promise<LayerSummary> {
  const body = JSON.stringify(payload);
  const response = await requestJson<LayerSummary>(`/api/v1/mapping/layers`, {
    method: 'POST',
    body
  });
  return response;
}

export async function importGeojsonLayer(form: FormData): Promise<GeoJSONImportResponse> {
  return requestMultipart<GeoJSONImportResponse>(`/api/v1/mapping/layers/geojson/import`, {
    method: 'POST',
    body: form
  });
}

export async function uploadGeojsonToLayer(layerId: string, form: FormData): Promise<RunAccepted> {
  if (!layerId) {
    throw new Error('Layer ID required');
  }
  return requestMultipart<RunAccepted>(`/api/v1/mapping/layers/${layerId}/geojson/upload`, {
    method: 'POST',
    body: form
  });
}

export async function listConnectors(params?: { limit?: number; connectorType?: string; slug?: string; search?: string }): Promise<ConnectorSummary[]> {
  const query = buildQuery({
    limit: params?.limit ?? 50,
    connectorType: params?.connectorType,
    slug: params?.slug,
    search: params?.search
  });
  const payload = await fetchJson<ConnectorListResponse>(`/api/v1/mapping/connectors${query}`);
  return payload.data ?? [];
}

export interface ConnectorCreateRequest {
  name: string;
  slug?: string | null;
  connector_type: 'http.geojson';
  source_uri: string;
  refresh_seconds?: number | null;
  auth?: { headers?: Record<string, string> } | null;
}

export async function createConnector(payload: ConnectorCreateRequest): Promise<ConnectorSummary> {
  const body = JSON.stringify({
    name: payload.name,
    slug: payload.slug ?? undefined,
    connector_type: payload.connector_type,
    source_uri: payload.source_uri,
    refresh_seconds: payload.refresh_seconds ?? undefined,
    auth: payload.auth ?? undefined
  });
  const response = await requestJson<ConnectorSummary>(`/api/v1/mapping/connectors`, {
    method: 'POST',
    body
  });
  return response;
}

export async function triggerConnectorRun(connectorId: string, payload?: { ingestion_config_id?: string; force?: boolean }): Promise<any> {
  if (!connectorId) {
    throw new Error('Connector ID required');
  }
  const body = payload ? JSON.stringify(payload) : undefined;
  return requestJson(`/api/v1/mapping/connectors/${connectorId}/runs`, {
    method: 'POST',
    body
  });
}

export async function listIngestionConfigs(params?: { limit?: number; source_layer_id?: string; external_source_id?: string }): Promise<IngestionConfigSummary[]> {
  const query = buildQuery({
    limit: params?.limit ?? 50,
    source_layer_id: params?.source_layer_id,
    external_source_id: params?.external_source_id
  });
  const payload = await fetchJson<IngestionConfigListResponse>(`/api/v1/mapping/ingestions${query}`);
  return payload.data ?? [];
}

export interface IngestionConfigCreateRequest {
  source_layer_id: string;
  external_source_id: string;
  name: string;
  enabled?: boolean;
  schedule_cron: string;
  source_format: 'geojson';
  ingest_strategy: 'replace_full' | 'upsert_merge';
  minzoom?: number | null;
  maxzoom?: number | null;
  managed_runtime?: { destination: 'mvt-artifact' } | null;
}

export async function createIngestionConfig(payload: IngestionConfigCreateRequest): Promise<IngestionConfigSummary> {
  const body = JSON.stringify({
    ...payload,
    managed_runtime: payload.managed_runtime ?? { destination: 'mvt-artifact' }
  });
  const response = await requestJson<IngestionConfigSummary>(`/api/v1/mapping/ingestions`, {
    method: 'POST',
    body
  });
  return response;
}

export async function triggerIngestionRun(configId: string): Promise<RunAccepted> {
  if (!configId) {
    throw new Error('Ingestion config ID required');
  }
  return requestJson<RunAccepted>(`/api/v1/mapping/ingestions/${configId}/run`, {
    method: 'POST'
  });
}

export async function getBasemapStyle(basemapId: string): Promise<any> {
  if (!basemapId) {
    throw new Error('basemap id required');
  }
  return fetchJson(`/api/v1/mapping/basemaps/${basemapId}/style`);
}

export interface MapViewCreateRequest {
  name: string;
  description?: string | null;
  renderer: 'maplibre';
  share_scope: ShareScope;
  map_settings: {
    center: [number, number];
    zoom: number;
    basemap?: { type: 'maplibre'; style: string } | null;
    theme?: 'light' | 'dark';
  };
  layers: any[];
}

export async function createMapView(payload: MapViewCreateRequest): Promise<MapViewSummary> {
  const body = JSON.stringify(payload);
  const response = await requestJson<MapViewSummary>(`/api/v1/mapping/map-views`, {
    method: 'POST',
    body
  });
  return response;
}

export interface StyleRule {
  when: string;
  paint: Record<string, unknown>;
  layout?: Record<string, unknown>;
  minzoom?: number;
  maxzoom?: number;
}

export interface LayerStyleOverride {
  type: 'point' | 'line' | 'fill' | 'symbol';
  rules: StyleRule[];
}

export interface ViewLayerConfig {
  layer_id: string;
  ordering?: number;
  overrides?: {
    visible?: boolean;
    minzoom?: number;
    maxzoom?: number;
    style?: LayerStyleOverride;
  } | null;
}

export async function setViewLayers(viewId: string, layers: ViewLayerConfig[]): Promise<void> {
  if (!viewId) {
    throw new Error('Map view ID required');
  }
  const body = JSON.stringify(layers);
  await requestJson(`/api/v1/mapping/map-views/${viewId}/layers`, {
    method: 'PUT',
    body
  });
}

export async function createBasemap(payload: BasemapCreateRequest): Promise<BasemapSummary> {
  const body = JSON.stringify(payload);
  const response = await requestJson<BasemapResponse>(`/api/v1/mapping/basemaps`, {
    method: 'POST',
    body
  });
  if (!response.data) {
    throw new Error('Create basemap response missing body');
  }
  return response.data;
}

export async function updateBasemap(id: string, payload: BasemapUpdateRequest): Promise<BasemapSummary> {
  if (!id) {
    throw new Error('Basemap ID required');
  }
  const body = JSON.stringify(payload);
  const response = await requestJson<BasemapResponse>(`/api/v1/mapping/basemaps/${id}`, {
    method: 'PATCH',
    body
  });
  if (!response.data) {
    throw new Error('Update basemap response missing body');
  }
  return response.data;
}

export async function listStyleThemes(params?: { share_scope?: ShareScope }): Promise<StyleTheme[]> {
  const query = buildQuery({ share_scope: params?.share_scope });
  const payload = await fetchJson<StyleThemeListResponse>(`/api/v1/mapping/style-themes${query}`);
  return payload.data ?? [];
}

export async function getStyleTheme(id: string): Promise<StyleTheme> {
  if (!id) {
    throw new Error('Style theme ID required');
  }
  const response = await requestJson<{ data?: StyleTheme }>(`/api/v1/mapping/style-themes/${id}`, { method: 'GET' });
  if (!response.data) {
    throw new Error('Style theme response missing body');
  }
  return response.data;
}

export async function createStyleTheme(payload: StyleThemeCreateRequest): Promise<StyleTheme> {
  const body = JSON.stringify(payload);
  const response = await requestJson<{ data?: StyleTheme }>(`/api/v1/mapping/style-themes`, {
    method: 'POST',
    body
  });
  if (!response.data) {
    throw new Error('Create style theme response missing body');
  }
  return response.data;
}

export async function updateStyleTheme(id: string, payload: StyleThemeUpdateRequest): Promise<StyleTheme> {
  if (!id) {
    throw new Error('Style theme ID required');
  }
  const body = JSON.stringify(payload);
  const response = await requestJson<{ data?: StyleTheme }>(`/api/v1/mapping/style-themes/${id}`, {
    method: 'PATCH',
    body
  });
  if (!response.data) {
    throw new Error('Update style theme response missing body');
  }
  return response.data;
}

export async function previewStyleTheme(payload: StyleThemePreviewRequest): Promise<StyleThemePreviewResponse> {
  const body = JSON.stringify(payload);
  return requestJson<StyleThemePreviewResponse>(`/api/v1/mapping/style-themes/preview`, {
    method: 'POST',
    body
  });
}

export async function getStyleThemeLayerMetadata(id: string): Promise<MapComponentMetadataResponse> {
  if (!id) {
    throw new Error('Layer ID required');
  }
  return fetchJson<MapComponentMetadataResponse>(`/api/v1/mapping/style-themes/layers/${id}/metadata`);
}

export interface MapSource {
  id?: string;
  tenant_id?: string | null;
  name?: string;
  description?: string | null;
  share_scope?: ShareScope;
  source_kind?: MapSourceKind;
  source_contract?: string | null;
  source_version?: number;
  pmtiles_key?: string | null;
  geojson_url?: string | null;
  auth_strategy?: GeoJSONHTTPAuthStrategy | null;
  auth_metadata?: GeoJSONHTTPAuthMetadata | null;
  bounds?: number[];
  min_zoom?: number | null;
  max_zoom?: number | null;
  vector_layers?: Array<Record<string, unknown> | string>;
  metadata?: Record<string, unknown> | null;
  request_recipe?: Record<string, unknown> | null;
  server_filtering?: Record<string, unknown> | null;
  is_enabled?: boolean;
  created_at?: string;
  updated_at?: string;
}

interface MapSourceListResponse {
  data?: MapSource[];
}

interface MapSourceResponse {
  data?: MapSource;
}

export interface MapSourceUploadPayload {
  file: File;
  name: string;
  description?: string | null;
  share_scope?: ShareScope;
  source_id?: string;
}

export interface GeoJSONFileMapSourceUploadPayload {
  file: File;
  name: string;
  description?: string | null;
  share_scope?: ShareScope;
  layer_id?: string;
  source_contract?: string;
  source_id?: string;
}

export interface MapSourceUploadInitRequest {
  filename: string;
  mime_type: string;
  size_bytes: number;
  checksum?: string;
  name: string;
  description?: string | null;
  share_scope: ShareScope;
  source_id?: string;
  processing_profile?: 'geojson_to_pmtiles';
  source_contract?: string;
  job_category?: string;
  subject_type?: string;
  subject_id?: string;
  layer_id?: string;
}

export interface MapSourceUploadPart {
  part_number?: number;
  etag?: string;
  size_bytes?: number;
}

export interface MapSourceUploadPartPresign {
  part_number?: number;
  url?: string;
  expires_at?: string;
}

export interface MapSourceUploadSession {
  id?: string;
  target_source_id?: string;
  completed_map_source_id?: string | null;
  name?: string;
  description?: string | null;
  share_scope?: ShareScope;
  processing_profile?: 'geojson_to_pmtiles';
  source_contract?: string | null;
  job_category?: string | null;
  subject_type?: string | null;
  subject_id?: string | null;
  layer_id?: string | null;
  filename?: string;
  mime_type?: string;
  size_bytes?: number;
  checksum?: string | null;
  status?: string;
  error_message?: string | null;
  processing_progress_percent?: number | null;
  processing_progress_message?: string | null;
  processing_logs?: string[];
  details?: Record<string, unknown> | null;
  uploaded_bytes?: number;
  uploaded_parts?: MapSourceUploadPart[];
  part_size_bytes?: number;
  max_parts?: number;
  total_parts?: number;
  expires_at?: string;
  created_at?: string;
  updated_at?: string;
  processing_started_at?: string | null;
  processing_completed_at?: string | null;
  map_source?: MapSource;
}

interface MapSourceUploadSessionResponse {
  data?: MapSourceUploadSession;
}

interface MapSourceUploadSessionListResponse {
  data?: MapSourceUploadSession[];
}

interface MapSourceUploadPartPresignResponse {
  data?: MapSourceUploadPartPresign;
}

export interface MapSourceUpdateRequest {
  name?: string;
  description?: string | null;
  share_scope?: ShareScope;
  is_enabled?: boolean;
  url?: string | null;
  request_recipe?: Record<string, unknown> | null;
  server_filtering?: Record<string, unknown> | null;
  auth_strategy?: GeoJSONHTTPAuthStrategy;
  auth_metadata?: GeoJSONHTTPAuthMetadata | null;
  auth_secret?: string | null;
  vector_layers?: Record<string, unknown>[];
}

export interface LiveGeoJSONMapSourceCreateRequest {
  name: string;
  description?: string | null;
  share_scope?: ShareScope;
  url?: string | null;
  request_recipe?: Record<string, unknown> | null;
  server_filtering?: Record<string, unknown> | null;
  auth_strategy?: GeoJSONHTTPAuthStrategy;
  auth_metadata?: GeoJSONHTTPAuthMetadata | null;
  auth_secret?: string | null;
}

export interface GeoJSONSourceQueryRequest {
  filter_sets?: Array<Record<string, unknown>>;
  active_filter_values?: Record<string, Record<string, unknown>>;
  context?: Record<string, unknown> | null;
}

export interface MapStyle {
  id?: string;
  tenant_id?: string | null;
  name?: string;
  description?: string | null;
  share_scope?: ShareScope;
  style_json?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
  is_enabled?: boolean;
  created_at?: string;
  updated_at?: string;
}

interface MapStyleListResponse {
  data?: MapStyle[];
}

interface MapStyleResponse {
  data?: MapStyle;
}

export interface SpriteSet {
  id?: string;
  tenant_id?: string | null;
  name?: string;
  description?: string | null;
  share_scope?: ShareScope;
  metadata?: Record<string, unknown> | null;
  is_enabled?: boolean;
  created_at?: string;
  updated_at?: string;
}

interface SpriteSetListResponse {
  data?: SpriteSet[];
}

interface SpriteSetResponse {
  data?: SpriteSet;
}

export interface SpriteSetUploadPayload {
  name: string;
  description?: string | null;
  share_scope?: ShareScope;
  sprite_json: File;
  sprite_png: File;
  sprite_json_2x: File;
  sprite_png_2x: File;
  sprite_preview_json?: File | null;
  sprite_preview_png?: File | null;
  sprite_preview_json_2x?: File | null;
  sprite_preview_png_2x?: File | null;
  sprite_set_id?: string;
}

export interface SpriteSetUpdateRequest {
  name?: string;
  description?: string | null;
  share_scope?: ShareScope;
  metadata?: Record<string, unknown> | null;
  is_enabled?: boolean;
}

export interface GlyphSet {
  id?: string;
  tenant_id?: string | null;
  name?: string;
  description?: string | null;
  share_scope?: ShareScope;
  fontstacks?: string[];
  metadata?: Record<string, unknown> | null;
  is_enabled?: boolean;
  created_at?: string;
  updated_at?: string;
}

interface GlyphSetListResponse {
  data?: GlyphSet[];
}

interface GlyphSetResponse {
  data?: GlyphSet;
}

export interface GlyphSetUploadPayload {
  name: string;
  description?: string | null;
  share_scope?: ShareScope;
  bundle: File;
  glyph_set_id?: string;
}

export interface GlyphSetUpdateRequest {
  name?: string;
  description?: string | null;
  share_scope?: ShareScope;
  metadata?: Record<string, unknown> | null;
  is_enabled?: boolean;
}

export interface MapStyleCreateRequest {
  name: string;
  share_scope: ShareScope;
  style_json: Record<string, unknown>;
  description?: string | null;
  metadata?: Record<string, unknown> | null;
  is_enabled?: boolean;
}

export interface MapStyleUpdateRequest {
  name?: string;
  share_scope?: ShareScope;
  style_json?: Record<string, unknown>;
  description?: string | null;
  metadata?: Record<string, unknown> | null;
  is_enabled?: boolean;
}

export interface MapStylePreviewRequest {
  style_json: Record<string, unknown>;
  metadata?: Record<string, unknown> | null;
}

export interface MapStylePreviewResponse {
  style_json?: Record<string, unknown> | null;
  view_state?: MapViewState | null;
}

export async function listMapSources(
  params?: { limit?: number; offset?: number; share_scope?: ShareScope },
  context?: MappingQueryContext | null,
): Promise<MapSource[]> {
  const query = buildMappingQueryParams({
    limit: params?.limit ?? 50,
    offset: params?.offset,
    share_scope: params?.share_scope
  }, context);
  const payload = await fetchJson<MapSourceListResponse>(`/api/v1/mapping/map-sources${query}`);
  return payload.data ?? [];
}

export async function getMapSource(id: string, context?: MappingQueryContext | null): Promise<MapSource> {
  if (!id) {
    throw new Error('Map source ID required');
  }
  const payload = await fetchJson<MapSourceResponse>(
    `/api/v1/mapping/map-sources/${id}${buildMappingQueryParams(undefined, context)}`,
  );
  if (!payload.data) {
    throw new Error('Map source response missing body');
  }
  return payload.data;
}

export async function queryMapSourceGeoJSON(
  id: string,
  payload: GeoJSONSourceQueryRequest,
  context?: MappingQueryContext | null,
): Promise<Record<string, unknown>> {
  if (!id) {
    throw new Error('Map source ID required');
  }
  return requestJson<Record<string, unknown>>(
    `/api/v1/mapping/map-sources/${id}/query.geojson${buildMappingQueryParams(undefined, context)}`,
    {
    method: 'POST',
    body: JSON.stringify(payload),
    },
  );
}

export async function uploadMapSource(payload: MapSourceUploadPayload): Promise<MapSource> {
  void payload;
  throw new Error('Direct PMTiles uploads are retired. Upload GeoJSON and let the platform build runtime artifacts.');
}

export async function uploadGeoJSONFileMapSource(payload: GeoJSONFileMapSourceUploadPayload): Promise<MapSource> {
  const formData = new FormData();
  formData.append('file', payload.file);
  formData.append('name', payload.name);
  if (payload.description) formData.append('description', payload.description);
  if (payload.share_scope) formData.append('share_scope', payload.share_scope);
  if (payload.layer_id) formData.append('layer_id', payload.layer_id);
  if (payload.source_contract) formData.append('source_contract', payload.source_contract);
  if (payload.source_id) formData.append('source_id', payload.source_id);

  const response = await requestMultipart<MapSourceResponse>(`/api/v1/mapping/map-sources/geojson-file`, {
    method: 'POST',
    body: formData
  });
  if (!response.data) {
    throw new Error('Upload GeoJSON map source response missing body');
  }
  return response.data;
}

export async function initMapSourceUpload(payload: MapSourceUploadInitRequest): Promise<MapSourceUploadSession> {
  const response = await requestJson<MapSourceUploadSessionResponse>(`/api/v1/map-source-uploads/init`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!response.data) {
    throw new Error('Map source upload init response missing body');
  }
  return response.data;
}

export async function getMapSourceUpload(id: string): Promise<MapSourceUploadSession> {
  if (!id) {
    throw new Error('Map source upload session ID required');
  }
  const response = await fetchJson<MapSourceUploadSessionResponse>(`/api/v1/map-source-uploads/${encodeURIComponent(id)}`);
  if (!response.data) {
    throw new Error('Map source upload session response missing body');
  }
  return response.data;
}

export async function listMapSourceUploads(options?: {
  limit?: number;
  source_id?: string;
  job_category?: string;
  subject_type?: string;
  subject_id?: string;
  status?: string;
}): Promise<MapSourceUploadSession[]> {
  const query = new URLSearchParams();
  if (options?.limit && options.limit > 0) {
    query.set('limit', String(options.limit));
  }
  if (options?.source_id?.trim()) {
    query.set('source_id', options.source_id.trim());
  }
  if (options?.job_category?.trim()) {
    query.set('job_category', options.job_category.trim());
  }
  if (options?.subject_type?.trim()) {
    query.set('subject_type', options.subject_type.trim());
  }
  if (options?.subject_id?.trim()) {
    query.set('subject_id', options.subject_id.trim());
  }
  if (options?.status?.trim()) {
    query.set('status', options.status.trim());
  }
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const response = await fetchJson<MapSourceUploadSessionListResponse>(`/api/v1/map-source-uploads${suffix}`);
  return response.data ?? [];
}

export async function presignMapSourceUploadPart(id: string, partNumber: number): Promise<MapSourceUploadPartPresign> {
  if (!id) {
    throw new Error('Map source upload session ID required');
  }
  if (!Number.isFinite(partNumber) || partNumber <= 0) {
    throw new Error('Map source upload part number must be greater than zero');
  }
  const query = buildQuery({ part_number: partNumber });
  const response = await fetchJson<MapSourceUploadPartPresignResponse>(
    `/api/v1/map-source-uploads/${encodeURIComponent(id)}/parts${query}`,
  );
  if (!response.data) {
    throw new Error('Map source upload presign response missing body');
  }
  return response.data;
}

export async function completeMapSourceUpload(id: string): Promise<MapSourceUploadSession> {
  if (!id) {
    throw new Error('Map source upload session ID required');
  }
  const response = await requestJson<MapSourceUploadSessionResponse>(`/api/v1/map-source-uploads/${encodeURIComponent(id)}/complete`, {
    method: 'POST',
  });
  if (!response.data) {
    throw new Error('Map source upload completion response missing body');
  }
  return response.data;
}

export async function abortMapSourceUpload(id: string): Promise<MapSourceUploadSession> {
  if (!id) {
    throw new Error('Map source upload session ID required');
  }
  const response = await requestJson<MapSourceUploadSessionResponse>(`/api/v1/map-source-uploads/${encodeURIComponent(id)}/abort`, {
    method: 'POST',
  });
  if (!response.data) {
    throw new Error('Map source upload abort response missing body');
  }
  return response.data;
}

export async function recalculateMapSourceSalesDistances(id: string): Promise<MapSourceUploadSession> {
  if (!id) {
    throw new Error('Map source ID required');
  }
  const response = await requestJson<MapSourceUploadSessionResponse>(
    `/api/v1/mapping/map-sources/${encodeURIComponent(id)}/sales-distance-recalculate`,
    {
      method: 'POST',
    },
  );
  if (!response.data) {
    throw new Error('Sales distance recalculation response missing body');
  }
  return response.data;
}

export async function createLiveGeoJSONMapSource(payload: LiveGeoJSONMapSourceCreateRequest): Promise<MapSource> {
  const response = await requestJson<MapSourceResponse>(`/api/v1/mapping/map-sources/geojson-http`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!response.data) {
    throw new Error('Create live GeoJSON source response missing body');
  }
  return response.data;
}

export async function updateMapSource(id: string, payload: MapSourceUpdateRequest): Promise<MapSource> {
  if (!id) {
    throw new Error('Map source ID required');
  }
  const body = JSON.stringify(payload);
  const response = await requestJson<MapSourceResponse>(`/api/v1/mapping/map-sources/${id}`, {
    method: 'PATCH',
    body
  });
  if (!response.data) {
    throw new Error('Update map source response missing body');
  }
  return response.data;
}

export async function deleteMapSource(id: string): Promise<void> {
  if (!id) {
    throw new Error('Map source ID required');
  }
  await requestJson<unknown>(`/api/v1/mapping/map-sources/${id}`, { method: 'DELETE' });
}

export async function listMapStyles(
  params?: { limit?: number; offset?: number; share_scope?: ShareScope },
  context?: MappingQueryContext | null,
): Promise<MapStyle[]> {
  const query = buildMappingQueryParams({
    limit: params?.limit ?? 50,
    offset: params?.offset,
    share_scope: params?.share_scope
  }, context);
  const payload = await fetchJson<MapStyleListResponse>(`/api/v1/mapping/map-styles${query}`);
  return payload.data ?? [];
}

export async function getMapStyle(id: string, context?: MappingQueryContext | null): Promise<MapStyle> {
  if (!id) {
    throw new Error('Map style ID required');
  }
  const payload = await fetchJson<MapStyleResponse>(
    `/api/v1/mapping/map-styles/${id}${buildMappingQueryParams(undefined, context)}`,
  );
  if (!payload.data) {
    throw new Error('Map style response missing body');
  }
  return rewriteMapStyleRuntimeSources(payload.data, context);
}

export async function createMapStyle(payload: MapStyleCreateRequest): Promise<MapStyle> {
  const body = JSON.stringify({
    ...payload,
    style_json: normalizeMapStyleSourcesForRequest(payload.style_json) ?? payload.style_json,
  });
  const response = await requestJson<MapStyleResponse>(`/api/v1/mapping/map-styles`, {
    method: 'POST',
    body
  });
  if (!response.data) {
    throw new Error('Create map style response missing body');
  }
  return response.data;
}

export async function updateMapStyle(id: string, payload: MapStyleUpdateRequest): Promise<MapStyle> {
  if (!id) {
    throw new Error('Map style ID required');
  }
  const body = JSON.stringify({
    ...payload,
    style_json: payload.style_json
      ? normalizeMapStyleSourcesForRequest(payload.style_json) ?? payload.style_json
      : payload.style_json,
  });
  const response = await requestJson<MapStyleResponse>(`/api/v1/mapping/map-styles/${id}`, {
    method: 'PATCH',
    body
  });
  if (!response.data) {
    throw new Error('Update map style response missing body');
  }
  return response.data;
}

export async function deleteMapStyle(id: string): Promise<void> {
  if (!id) {
    throw new Error('Map style ID required');
  }
  await requestJson<unknown>(`/api/v1/mapping/map-styles/${id}`, { method: 'DELETE' });
}

export async function previewMapStyle(payload: MapStylePreviewRequest): Promise<MapStylePreviewResponse> {
  const body = JSON.stringify({
    ...payload,
    style_json: normalizeMapStyleSourcesForRequest(payload.style_json) ?? payload.style_json,
  });
  return requestJson<MapStylePreviewResponse>(`/api/v1/mapping/map-styles/preview`, {
    method: 'POST',
    body
  });
}

export async function executeMapExperienceResolver(
  payload: MapExperienceResolverExecutionRequest,
): Promise<RuntimeFeatureStatePatch> {
  return requestJson<RuntimeFeatureStatePatch>(`/api/v1/mapping/map-interactivity/resolvers/execute`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function listMapSourceShares(id: string): Promise<TenantShare[]> {
  if (!id) {
    throw new Error('Map source ID required');
  }
  const payload = await fetchJson<TenantShareListResponse>(`/api/v1/mapping/map-sources/${id}/shares`);
  return payload.data ?? [];
}

export async function replaceMapSourceShares(id: string, tenantIds: string[]): Promise<TenantShare[]> {
  if (!id) {
    throw new Error('Map source ID required');
  }
  const body = JSON.stringify({ tenant_ids: tenantIds });
  const payload = await requestJson<TenantShareListResponse>(`/api/v1/mapping/map-sources/${id}/shares`, {
    method: 'PUT',
    body
  });
  return payload.data ?? [];
}

export async function listMapStyleShares(id: string): Promise<TenantShare[]> {
  if (!id) {
    throw new Error('Map style ID required');
  }
  const payload = await fetchJson<TenantShareListResponse>(`/api/v1/mapping/map-styles/${id}/shares`);
  return payload.data ?? [];
}

export async function replaceMapStyleShares(id: string, tenantIds: string[]): Promise<TenantShare[]> {
  if (!id) {
    throw new Error('Map style ID required');
  }
  const body = JSON.stringify({ tenant_ids: tenantIds });
  const payload = await requestJson<TenantShareListResponse>(`/api/v1/mapping/map-styles/${id}/shares`, {
    method: 'PUT',
    body
  });
  return payload.data ?? [];
}

export async function listSpriteSets(params?: { limit?: number; offset?: number; share_scope?: ShareScope }): Promise<SpriteSet[]> {
  const query = buildQuery({
    limit: params?.limit ?? 50,
    offset: params?.offset,
    share_scope: params?.share_scope
  });
  const payload = await fetchJson<SpriteSetListResponse>(`/api/v1/mapping/sprite-sets${query}`);
  return payload.data ?? [];
}

export async function getSpriteSet(id: string): Promise<SpriteSet> {
  if (!id) {
    throw new Error('Sprite set ID required');
  }
  const payload = await fetchJson<SpriteSetResponse>(`/api/v1/mapping/sprite-sets/${id}`);
  if (!payload.data) {
    throw new Error('Sprite set response missing body');
  }
  return payload.data;
}

export async function getSpriteSetManifest(id: string, options?: { variant?: 'preview' }): Promise<SpriteManifest> {
  if (!id) {
    throw new Error('Sprite set ID required');
  }
  const query = options?.variant === 'preview' ? '?variant=preview' : '';
  return fetchJson<SpriteManifest>(`/api/v1/mapping/sprite-sets/${id}/sprite.json${query}`);
}

export async function getSpriteSetImage(id: string, options?: { variant?: 'preview' }): Promise<Blob> {
  if (!id) {
    throw new Error('Sprite set ID required');
  }
  const query = options?.variant === 'preview' ? '?variant=preview' : '';
  return fetchBlob(`/api/v1/mapping/sprite-sets/${id}/sprite.png${query}`);
}

export async function uploadSpriteSet(payload: SpriteSetUploadPayload): Promise<SpriteSet> {
  const formData = new FormData();
  formData.append('name', payload.name);
  formData.append('sprite_json', payload.sprite_json);
  formData.append('sprite_png', payload.sprite_png);
  formData.append('sprite_json_2x', payload.sprite_json_2x);
  formData.append('sprite_png_2x', payload.sprite_png_2x);
  if (payload.sprite_preview_json) formData.append('sprite_preview_json', payload.sprite_preview_json);
  if (payload.sprite_preview_png) formData.append('sprite_preview_png', payload.sprite_preview_png);
  if (payload.sprite_preview_json_2x) formData.append('sprite_preview_json_2x', payload.sprite_preview_json_2x);
  if (payload.sprite_preview_png_2x) formData.append('sprite_preview_png_2x', payload.sprite_preview_png_2x);
  if (payload.description) formData.append('description', payload.description);
  if (payload.share_scope) formData.append('share_scope', payload.share_scope);
  if (payload.sprite_set_id) formData.append('sprite_set_id', payload.sprite_set_id);

  const response = await requestMultipart<SpriteSetResponse>(`/api/v1/mapping/sprite-sets/upload`, {
    method: 'POST',
    body: formData
  });
  if (!response.data) {
    throw new Error('Upload sprite set response missing body');
  }
  return response.data;
}

export async function updateSpriteSet(id: string, payload: SpriteSetUpdateRequest): Promise<SpriteSet> {
  if (!id) {
    throw new Error('Sprite set ID required');
  }
  const body = JSON.stringify(payload);
  const response = await requestJson<SpriteSetResponse>(`/api/v1/mapping/sprite-sets/${id}`, {
    method: 'PATCH',
    body
  });
  if (!response.data) {
    throw new Error('Update sprite set response missing body');
  }
  return response.data;
}

export async function deleteSpriteSet(id: string): Promise<void> {
  if (!id) {
    throw new Error('Sprite set ID required');
  }
  await requestJson<unknown>(`/api/v1/mapping/sprite-sets/${id}`, { method: 'DELETE' });
}

export async function listSpriteSetShares(id: string): Promise<TenantShare[]> {
  if (!id) {
    throw new Error('Sprite set ID required');
  }
  const payload = await fetchJson<TenantShareListResponse>(`/api/v1/mapping/sprite-sets/${id}/shares`);
  return payload.data ?? [];
}

export async function replaceSpriteSetShares(id: string, tenantIds: string[]): Promise<TenantShare[]> {
  if (!id) {
    throw new Error('Sprite set ID required');
  }
  const body = JSON.stringify({ tenant_ids: tenantIds });
  const payload = await requestJson<TenantShareListResponse>(`/api/v1/mapping/sprite-sets/${id}/shares`, {
    method: 'PUT',
    body
  });
  return payload.data ?? [];
}

export async function listGlyphSets(params?: { limit?: number; offset?: number; share_scope?: ShareScope }): Promise<GlyphSet[]> {
  const query = buildQuery({
    limit: params?.limit ?? 50,
    offset: params?.offset,
    share_scope: params?.share_scope
  });
  const payload = await fetchJson<GlyphSetListResponse>(`/api/v1/mapping/glyph-sets${query}`);
  return payload.data ?? [];
}

export async function getGlyphSet(id: string): Promise<GlyphSet> {
  if (!id) {
    throw new Error('Glyph set ID required');
  }
  const payload = await fetchJson<GlyphSetResponse>(`/api/v1/mapping/glyph-sets/${id}`);
  if (!payload.data) {
    throw new Error('Glyph set response missing body');
  }
  return payload.data;
}

export async function uploadGlyphSet(payload: GlyphSetUploadPayload): Promise<GlyphSet> {
  const formData = new FormData();
  formData.append('name', payload.name);
  formData.append('bundle', payload.bundle);
  if (payload.description) formData.append('description', payload.description);
  if (payload.share_scope) formData.append('share_scope', payload.share_scope);
  if (payload.glyph_set_id) formData.append('glyph_set_id', payload.glyph_set_id);

  const response = await requestMultipart<GlyphSetResponse>(`/api/v1/mapping/glyph-sets/upload`, {
    method: 'POST',
    body: formData
  });
  if (!response.data) {
    throw new Error('Upload glyph set response missing body');
  }
  return response.data;
}

export async function updateGlyphSet(id: string, payload: GlyphSetUpdateRequest): Promise<GlyphSet> {
  if (!id) {
    throw new Error('Glyph set ID required');
  }
  const body = JSON.stringify(payload);
  const response = await requestJson<GlyphSetResponse>(`/api/v1/mapping/glyph-sets/${id}`, {
    method: 'PATCH',
    body
  });
  if (!response.data) {
    throw new Error('Update glyph set response missing body');
  }
  return response.data;
}

export async function deleteGlyphSet(id: string): Promise<void> {
  if (!id) {
    throw new Error('Glyph set ID required');
  }
  await requestJson<unknown>(`/api/v1/mapping/glyph-sets/${id}`, { method: 'DELETE' });
}

export async function listGlyphSetShares(id: string): Promise<TenantShare[]> {
  if (!id) {
    throw new Error('Glyph set ID required');
  }
  const payload = await fetchJson<TenantShareListResponse>(`/api/v1/mapping/glyph-sets/${id}/shares`);
  return payload.data ?? [];
}

export async function replaceGlyphSetShares(id: string, tenantIds: string[]): Promise<TenantShare[]> {
  if (!id) {
    throw new Error('Glyph set ID required');
  }
  const body = JSON.stringify({ tenant_ids: tenantIds });
  const payload = await requestJson<TenantShareListResponse>(`/api/v1/mapping/glyph-sets/${id}/shares`, {
    method: 'PUT',
    body
  });
  return payload.data ?? [];
}
